<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import KanbanSummaryCards from 'kanban/components/reports/KanbanSummaryCards.vue';
import KanbanFunnelChart from 'kanban/components/reports/KanbanFunnelChart.vue';
import {
  formatNumber,
  formatDuration,
  formatCurrencyValue,
  computeTrend,
} from 'kanban/helper/reportFormatters';
import { useHomeDashboard } from 'vinctoHome/composables/useHomeDashboard';
import KpiCard from 'vinctoHome/components/KpiCard.vue';
import RangeFilter from 'vinctoHome/components/RangeFilter.vue';
import ActionList from 'vinctoHome/components/ActionList.vue';
import TodayDates from 'vinctoHome/components/TodayDates.vue';
import TrendLineChart from 'vinctoHome/components/TrendLineChart.vue';
import RfmDonut from 'vinctoHome/components/RfmDonut.vue';
import HomeAgentTable from 'vinctoHome/components/HomeAgentTable.vue';
import LockedModuleCard from 'vinctoHome/components/LockedModuleCard.vue';
import PanelEmptyState from 'vinctoHome/components/PanelEmptyState.vue';
import DealsTab from 'vinctoHome/components/tabs/DealsTab.vue';
import ServiceTab from 'vinctoHome/components/tabs/ServiceTab.vue';
import ActivitiesTab from 'vinctoHome/components/tabs/ActivitiesTab.vue';
import { usePanelTabs, lockedAreas } from 'vinctoHome/composables/usePanelTabs';

const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();
const currentUser = useMapGetter('getCurrentUser');

const {
  uiFlags,
  fetchDashboard,
  refreshDashboard,
  live,
  summary,
  timeseries,
  agents,
  kanban,
  crmToday,
  segments,
  activities,
  meta,
  modules,
  salesContact,
} = useHomeDashboard();

// Visao da empresa: admin ou funcao personalizada com o Painel geral. Quem
// decide e o servidor (meta.role), nunca o papel lido no navegador.
const isCompanyView = computed(() => meta.value?.role === 'administrator');

const { tabs, activeKey, setTab } = usePanelTabs(modules);
const TAB_LABELS = computed(() => ({
  geral: t('VINCTO_HOME.TABS.OVERVIEW'),
  negocios: t('VINCTO_HOME.TABS.DEALS'),
  atendimento: t('VINCTO_HOME.TABS.SERVICE'),
  atividades: t('VINCTO_HOME.TABS.ACTIVITIES'),
}));
const tabBarTabs = computed(() =>
  tabs.value.map(tab => ({ key: tab.key, label: TAB_LABELS.value[tab.key] }))
);
const activeTabIndex = computed(() =>
  tabs.value.findIndex(tab => tab.key === activeKey.value)
);
const activeArea = computed(
  () => tabs.value.find(tab => tab.key === activeKey.value)?.area
);
const activeAreaState = computed(() => modules.value[activeArea.value]);
const locked = computed(() => lockedAreas(modules.value));

const RANGES = ['today', '7d', '30d'];
const range = ref(
  RANGES.includes(route.query.range) ? route.query.range : '7d'
);

const params = computed(() => ({ range: range.value }));

const setRange = async value => {
  range.value = value;
  router.replace({ query: { ...route.query, range: value } });
  uiFlags.value.isLoading = true;
  await fetchDashboard(params.value);
};

// Refresh ao vivo (60s) so das secoes "agora" — pausa com a aba oculta.
const { startRefetching } = useLiveRefresh(async () => {
  if (document.hidden) return;
  await refreshDashboard(params.value);
});

onMounted(async () => {
  await fetchDashboard(params.value);
  // Endpoint inexistente (build antigo do backend): volta 1x pras conversas.
  if (uiFlags.value.notFound) {
    router.replace({
      name: 'home',
      params: { accountId: route.params.accountId },
    });
    return;
  }
  startRefetching();
});

const greeting = computed(() => {
  const hour = new Date().getHours();
  const key =
    // eslint-disable-next-line no-nested-ternary
    hour < 12 ? 'MORNING' : hour < 18 ? 'AFTERNOON' : 'EVENING';
  return t(`VINCTO_HOME.GREETING.${key}`, {
    name: currentUser.value?.name?.split(' ')[0] || '',
  });
});

const isLoading = computed(() => uiFlags.value.isLoading);

const currency = computed(() => kanban.value?.summary?.currency || 'BRL');

const kpis = computed(() => {
  const current = summary.value || {};
  const previous = current.previous || {};
  const cards = [
    {
      key: 'open',
      icon: 'i-lucide-message-circle',
      label: t('VINCTO_HOME.KPI.OPEN'),
      value: formatNumber(live.value?.open, locale.value),
      to: { name: 'home' },
    },
    {
      key: 'unattended',
      icon: 'i-lucide-clock-alert',
      label: t('VINCTO_HOME.KPI.UNATTENDED'),
      value: formatNumber(live.value?.unattended, locale.value),
      accent: !!live.value?.unattended,
      to: { name: 'conversation_unattended' },
    },
    {
      key: 'first_response',
      icon: 'i-lucide-timer',
      label: t('VINCTO_HOME.KPI.FIRST_RESPONSE'),
      value: formatDuration(current.avg_first_response_time, t),
      trend: computeTrend(
        current.avg_first_response_time,
        previous.avg_first_response_time
      ),
      invert: true,
    },
    {
      key: 'resolved',
      icon: 'i-lucide-check-check',
      label: t('VINCTO_HOME.KPI.RESOLVED'),
      value: formatNumber(current.resolutions_count, locale.value),
      trend: computeTrend(
        current.resolutions_count,
        previous.resolutions_count
      ),
    },
  ];

  const quick = kanban.value?.quick;
  if (quick) {
    cards.push({
      key: 'pipeline',
      icon: 'i-lucide-circle-dollar-sign',
      label: t('VINCTO_HOME.KPI.PIPELINE'),
      value: formatCurrencyValue(
        quick.pipeline_value,
        currency.value,
        locale.value,
        {
          compact: true,
        }
      ),
      to: { name: 'kanban_list' },
    });
  }
  const kanbanSummary = kanban.value?.summary;
  if (kanbanSummary) {
    cards.push({
      key: 'deals_won',
      icon: 'i-lucide-trophy',
      label: t('VINCTO_HOME.KPI.DEALS_WON'),
      value: formatNumber(kanbanSummary.current?.deals_won, locale.value),
      trend: computeTrend(
        kanbanSummary.current?.deals_won,
        kanbanSummary.previous?.deals_won
      ),
      to: { name: 'kanban_list' },
    });
  }
  return cards;
});

const goToConversations = () => {
  router.push({ name: 'home', params: { accountId: route.params.accountId } });
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="flex flex-col w-full max-w-6xl gap-4 p-6 mx-auto">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex flex-col gap-1">
          <h1 class="text-xl font-semibold tracking-[-0.02em] text-n-slate-12">
            {{ greeting }}
          </h1>
          <p class="text-sm text-n-slate-10">
            {{ t('VINCTO_HOME.SUBTITLE') }}
          </p>
        </div>
        <RangeFilter :model-value="range" @update:model-value="setRange" />
      </div>
      <div
        v-if="isCompanyView && tabBarTabs.length > 1"
        class="min-w-0 max-w-full max-sm:overflow-x-auto max-sm:no-scrollbar"
      >
        <TabBar
          :tabs="tabBarTabs"
          :initial-active-tab="activeTabIndex"
          @tab-changed="tab => setTab(tab.key)"
        />
      </div>

      <div
        v-if="uiFlags.error && !uiFlags.notFound"
        class="flex flex-col items-center justify-center gap-3 py-16 text-center"
      >
        <i class="i-lucide-alert-triangle w-8 h-8 text-n-amber-11" />
        <p class="text-sm text-n-slate-11">{{ t('VINCTO_HOME.ERROR') }}</p>
        <div class="flex items-center gap-2">
          <Button
            size="sm"
            variant="faded"
            color="slate"
            icon="i-lucide-rotate-cw"
            :label="t('VINCTO_HOME.RETRY')"
            @click="fetchDashboard(params)"
          />
          <Button
            size="sm"
            variant="faded"
            color="blue"
            icon="i-lucide-message-circle"
            :label="t('VINCTO_HOME.GO_TO_CONVERSATIONS')"
            @click="goToConversations"
          />
        </div>
      </div>

      <template v-else-if="activeKey !== 'geral'">
        <PanelEmptyState
          v-if="activeAreaState === 'empty'"
          :area="activeArea"
        />
        <DealsTab
          v-else-if="activeKey === 'negocios'"
          :range="range"
          :boards="kanban?.boards || []"
        />
        <ServiceTab
          v-else-if="activeKey === 'atendimento'"
          :range="range"
          :live="live || {}"
          :summary="summary || {}"
        />
        <ActivitiesTab v-else-if="activeKey === 'atividades'" :range="range" />
      </template>
      <template v-else>
        <div
          v-if="isLoading"
          class="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6 animate-pulse"
        >
          <div
            v-for="i in 6"
            :key="i"
            class="h-28 rounded-2xl border border-n-weak bg-white dark:bg-n-solid-2"
          />
        </div>
        <div
          v-else
          class="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6"
        >
          <KpiCard
            v-for="card in kpis"
            :key="card.key"
            :label="card.label"
            :value="card.value"
            :icon="card.icon"
            :trend="card.trend"
            :invert="card.invert"
            :accent="card.accent"
            :to="card.to"
          />
        </div>

        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ActionList
            :live="live"
            :kanban-quick="kanban?.quick"
            :activities="activities"
            :is-loading="isLoading"
          />
          <TodayDates
            v-if="crmToday"
            :dates="crmToday"
            :is-loading="isLoading"
          />
        </div>

        <TrendLineChart :points="timeseries" :is-loading="isLoading" />

        <template v-if="isCompanyView">
          <KanbanSummaryCards
            v-if="kanban?.summary"
            class="!rounded-2xl !border-n-weak !bg-white dark:!bg-n-solid-2"
            :data="kanban.summary"
            :is-loading="isLoading"
            :locale="locale"
          />
          <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <KanbanFunnelChart
              v-if="kanban?.funnel"
              class="!rounded-2xl !border-n-weak !bg-white dark:!bg-n-solid-2"
              :data="kanban.funnel"
              :is-loading="isLoading"
              :locale="locale"
            />
            <RfmDonut
              v-if="segments"
              :data="segments"
              :is-loading="isLoading"
            />
          </div>
          <HomeAgentTable
            v-if="agents"
            :agents="agents"
            :is-loading="isLoading"
          />
          <div
            v-if="locked.length"
            class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
          >
            <LockedModuleCard
              v-for="area in locked"
              :key="area"
              :area="area"
              :contact="salesContact"
            />
          </div>
        </template>
      </template>
    </div>
  </div>
</template>
